import { useApolloClient, useMutation } from '@apollo/client/react';
import {
  Box,
  Text,
  VStack,
  Button,
  Switch,
  Alert,
  HStack,
  SimpleGrid,
  Badge,
  Input,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';

import { AiModelCombobox } from '#src/components/ai-model-combobox';
import { useColorMode } from '#src/components/ui/color-mode';
import { toaster } from '#src/components/ui/toaster';
import { UPDATE_MY_USER_SETTINGS } from '#src/graphql/mutations/update-my-user-settings';
import { UPDATE_USER_PROFILE } from '#src/graphql/mutations/update-user-profile';
import { ME } from '#src/graphql/queries/me';
import {
  useAppConfig,
  SIDEBAR_TOGGLE_POSITIONS,
  type SidebarTogglePosition,
} from '#src/providers/app-config-provider';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { useCurrentUser, useUser } from '#src/providers/user-provider';

export function SettingsPage() {
  const client = useApolloClient();
  const user = useCurrentUser();
  const { logout, refetchMe } = useUser();
  const { config, clearField, setSidebarTogglePosition } = useAppConfig();
  const { setColorMode } = useColorMode();
  const appSettings = useAppSettings();
  const [updateMyUserSettings, { loading: savingAiModel }] = useMutation(
    UPDATE_MY_USER_SETTINGS,
  );
  const [updateUserProfile, { loading: savingDisplayName }] =
    useMutation(UPDATE_USER_PROFILE);
  const [displayName, setDisplayName] = useState(user.displayName);

  useEffect(() => {
    setDisplayName(user.displayName);
  }, [user.displayName]);

  const modelOptions = useMemo(
    () =>
      appSettings.aiAssistEditChatModels.length > 0
        ? appSettings.aiAssistEditChatModels
        : [appSettings.defaultAiAssistEditChatModel],
    [
      appSettings.aiAssistEditChatModels,
      appSettings.defaultAiAssistEditChatModel,
    ],
  );

  const aiModelComboboxValue =
    user.settings?.aiAssistEditChatModel ?? user.effectiveAiAssistEditChatModel;

  const persistAiModel = async (aiAssistEditChatModel: string | null) => {
    try {
      await updateMyUserSettings({
        variables: { input: { aiAssistEditChatModel } },
      });
      await refetchMe();
      toaster.create({
        title: 'Saved',
        description:
          aiAssistEditChatModel == null
            ? 'AI model reset to the deployment default.'
            : 'AI model preference updated.',
        type: 'success',
        duration: 2500,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not save AI model.',
        type: 'error',
      });
    }
  };
  const trimmedDisplayName = displayName.trim();
  const isDisplayNameChanged = trimmedDisplayName !== user.displayName;
  const isDisplayNameInvalid = trimmedDisplayName.length === 0;

  const persistDisplayName = async () => {
    if (!isDisplayNameChanged || isDisplayNameInvalid) {
      return;
    }
    try {
      const result = await updateUserProfile({
        variables: { input: { displayName: trimmedDisplayName } },
      });
      const updatedUser = result.data?.updateUserProfile;
      if (updatedUser) {
        client.writeQuery({
          query: ME,
          data: { me: updatedUser },
        });
      }
      toaster.create({
        title: 'Saved',
        description: 'Display name updated.',
        type: 'success',
        duration: 2500,
        closable: true,
      });
    } catch (err) {
      toaster.create({
        title: 'Error',
        description:
          err instanceof Error ? err.message : 'Could not save display name.',
        type: 'error',
      });
    }
  };

  return (
    <Box>
      <Box bg="bg" shadow="lg" borderRadius="lg" p={6}>
        <VStack gap={6} align="stretch">
          <Alert.Root status="info">
            <Alert.Indicator />
            <Alert.Title>
              Sidebar position and recent documents are stored in this browser.
              Account preferences (AI model, theme) sync to your account.
            </Alert.Title>
          </Alert.Root>

          <Box>
            <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
              User
            </Text>
            <Text fontSize="xs" color="fg.muted" mb={2}>
              Signed in as
            </Text>
            <HStack gap={3} align="center">
              <Text fontSize="md" fontWeight="medium" color="fg">
                {user.email}
              </Text>
              <Button
                variant="outline"
                colorScheme="gray"
                size="sm"
                onClick={() => {
                  void logout().then(() => {
                    toaster.create({
                      title: 'Logged out',
                      description: 'You have been signed out.',
                      type: 'info',
                      duration: 3000,
                      closable: true,
                    });
                  });
                }}
              >
                Log out
              </Button>
            </HStack>
            <VStack gap={2} align="stretch" maxW="md" mt={3}>
              <Input
                value={displayName}
                onChange={(e) => {
                  setDisplayName(e.target.value);
                }}
                placeholder="Display name"
                maxLength={80}
              />
              <HStack gap={2}>
                <Button
                  size="sm"
                  onClick={() => {
                    void persistDisplayName();
                  }}
                  disabled={
                    savingDisplayName ||
                    !isDisplayNameChanged ||
                    isDisplayNameInvalid
                  }
                >
                  Save display name
                </Button>
                {isDisplayNameChanged && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setDisplayName(user.displayName);
                    }}
                    disabled={savingDisplayName}
                  >
                    Reset
                  </Button>
                )}
              </HStack>
            </VStack>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
              AI model
            </Text>
            <Text fontSize="xs" color="fg.muted" mb={3}>
              OpenAI model for document AI edits, Ask AI, and workspace Q&A.
              Stored on the server.
            </Text>
            <VStack gap={2} align="stretch" maxW="md">
              <AiModelCombobox
                value={aiModelComboboxValue}
                onValueChange={(value) => {
                  void persistAiModel(value);
                }}
                options={modelOptions}
                disabled={savingAiModel}
              />
              <Text fontSize="xs" color="fg.muted">
                Effective model: {user.effectiveAiAssistEditChatModel}
                {user.settings?.aiAssistEditChatModel == null
                  ? ' (deployment default)'
                  : ''}
              </Text>
              {user.settings?.aiAssistEditChatModel != null && (
                <Button
                  size="xs"
                  variant="outline"
                  alignSelf="flex-start"
                  disabled={savingAiModel}
                  onClick={() => {
                    void persistAiModel(null);
                  }}
                >
                  Use deployment default
                </Button>
              )}
            </VStack>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
              Sidebar toggle position
            </Text>
            <Text fontSize="xs" color="fg.muted" mb={3}>
              Where the menu button and sidebar appear
            </Text>
            <SimpleGrid columns={2} gap={2}>
              {SIDEBAR_TOGGLE_POSITIONS.map((p) => {
                const selected = config.sidebarTogglePosition === p;
                const label = p
                  .split('-')
                  .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                  .join(' ');
                return (
                  <Button
                    key={p}
                    size="sm"
                    variant={selected ? 'solid' : 'outline'}
                    colorScheme={selected ? 'blue' : 'gray'}
                    onClick={() => {
                      setSidebarTogglePosition(p as SidebarTogglePosition);
                      toaster.create({
                        title: 'Updated',
                        description: `Sidebar toggle: ${label}`,
                        type: 'success',
                        duration: 2000,
                        closable: true,
                      });
                    }}
                  >
                    {label}
                  </Button>
                );
              })}
            </SimpleGrid>
          </Box>

          <Box>
            <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
              Recent documents
            </Text>
            <Text fontSize="xs" color="fg.muted" mb={2}>
              Clear the list of recently opened documents shown in the sidebar
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme="gray"
              onClick={() => {
                clearField('recentlyOpenedDocuments');
                toaster.create({
                  title: 'Cleared',
                  description: 'Recent documents list has been cleared.',
                  type: 'success',
                  duration: 3000,
                  closable: true,
                });
              }}
            >
              Clear recent documents
            </Button>
          </Box>

          <Box borderTop="1px" borderColor="border" pt={4} />

          {(appSettings.localEndpoints === true ||
            appSettings.localDatabase === true) && (
            <Box>
              <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
                Backend
              </Text>
              <Text fontSize="xs" color="fg.muted" mb={2}>
                Current environment indicators
              </Text>
              <HStack gap={2} flexWrap="wrap">
                {appSettings.localEndpoints === true && (
                  <Badge colorPalette="blue" size="sm">
                    Local endpoints (dev)
                  </Badge>
                )}
                {appSettings.localDatabase === true ? (
                  <Badge colorPalette="green" size="sm">
                    Local database
                  </Badge>
                ) : (
                  <Badge colorPalette="red" size="sm">
                    Remote database
                  </Badge>
                )}
              </HStack>
            </Box>
          )}

          <Box borderTop="1px" borderColor="border" pt={4} />

          <Box>
            <Text fontSize="sm" fontWeight="medium" color="fg" mb={2}>
              Appearance
            </Text>
            <Text fontSize="xs" color="fg.muted" mb={3}>
              Choose light or dark theme. Saved to your account.
            </Text>
            <HStack align="center" gap={2}>
              <Switch.Root
                checked={config.colorMode === 'dark'}
                onCheckedChange={(e) => {
                  const next = e.checked ? 'dark' : 'light';
                  setColorMode(next);
                  toaster.create({
                    title: 'Theme updated',
                    description: `Using ${next} mode`,
                    type: 'success',
                    duration: 2000,
                    closable: true,
                  });
                }}
              >
                <Switch.HiddenInput />
                <Switch.Control>
                  <Switch.Thumb />
                </Switch.Control>
              </Switch.Root>
              <Text fontSize="sm" color="fg.muted">
                Dark mode
              </Text>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}
